export type CrosswordDirection = 'across' | 'down';

export type CrosswordClue = {
  id: string;
  number: number;
  direction: CrosswordDirection;
  start: number;
  length: number;
  answer: string;
  clue: string;
};

export type MiniCrosswordPuzzle = {
  size: 4 | 5;
  solution: string[];
  blocks: boolean[];
  clues: CrosswordClue[];
  generationAttempts: number;
  fallbackUsed: boolean;
  layout: string;
  goodClueCount: number;
};

export function isMiniCrosswordPuzzle(value: unknown): value is MiniCrosswordPuzzle {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<MiniCrosswordPuzzle>;
  if (
    !Number.isInteger(candidate.size) ||
    (candidate.size !== 4 && candidate.size !== 5) ||
    !Array.isArray(candidate.solution) ||
    candidate.solution.length !== candidate.size! ** 2 ||
    !candidate.solution.every((cell) => cell === '#' || /^[A-Z]$/.test(cell)) ||
    !Array.isArray(candidate.blocks) ||
    candidate.blocks.length !== candidate.solution.length ||
    !candidate.blocks.every((block) => typeof block === 'boolean') ||
    !candidate.blocks.every((block, index) => block === (candidate.solution![index] === '#')) ||
    !Array.isArray(candidate.clues) ||
    !candidate.clues.every(isCrosswordClue) ||
    !Number.isInteger(candidate.generationAttempts) ||
    candidate.generationAttempts! < 1 ||
    typeof candidate.fallbackUsed !== 'boolean' ||
    typeof candidate.layout !== 'string' ||
    candidate.layout.trim().length === 0 ||
    candidate.goodClueCount !== candidate.clues.length ||
    (candidate.size === 5 ? candidate.clues.length !== 10 : candidate.clues.length !== 8)
  ) {
    return false;
  }
  const clueIds = new Set(candidate.clues.map((clue) => clue.id));
  const answers = new Set(candidate.clues.map((clue) => clue.answer));
  if (clueIds.size !== candidate.clues.length || answers.size !== candidate.clues.length)
    return false;
  const coverage = candidate.solution.map(() => 0);
  for (const clue of candidate.clues) {
    for (let offset = 0; offset < clue.length; offset += 1) {
      const index =
        clue.direction === 'across' ? clue.start + offset : clue.start + offset * candidate.size!;
      if (candidate.blocks[index] || candidate.solution[index] !== clue.answer[offset])
        return false;
      coverage[index] += 1;
    }
  }
  return candidate.solution.every((cell, index) => cell === '#' || coverage[index] === 2);
}

function isCrosswordClue(value: unknown): value is CrosswordClue {
  if (!value || typeof value !== 'object') return false;
  const clue = value as Partial<CrosswordClue>;
  return (
    typeof clue.id === 'string' &&
    clue.id.length > 0 &&
    Number.isInteger(clue.number) &&
    clue.number! > 0 &&
    (clue.direction === 'across' || clue.direction === 'down') &&
    Number.isInteger(clue.start) &&
    clue.start! >= 0 &&
    Number.isInteger(clue.length) &&
    clue.length! >= 2 &&
    typeof clue.answer === 'string' &&
    clue.answer.length === clue.length &&
    /^[A-Z]+$/.test(clue.answer) &&
    typeof clue.clue === 'string' &&
    clue.clue.trim().length > 0
  );
}
